import React, { FunctionComponent, Suspense, useContext, useMemo, useState } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { useSelector } from 'react-redux';
import { DocumentSummary } from '../../graphql/entities';
import { default as Form } from '../../components/connectedForm';
import ModalContext from '../../components/Context/ModalContext';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { ViewContextProvider } from '../../components/Context/ViewContext';
import ConnectedFormContext, { ConnectedFormContextType } from '../../components/Context/ConnectedFormContext';
import { Direction, IDocumentSummary, IFilterInput, Maybe, TargetEntity } from '../../graphql/schema/p4types';
import { useWorkspace, WorkspaceConfig } from '../../graphql/hooks/useWorkspace';
import { Paths } from '../../@types/keyOfNested';
import FluentModal from '../../components/modals/FluentModal';

//Dynamic imports for nested forms.
const LazyCustomerForm = React.lazy(() => import('../../views/customer/CustomerForm'));
const LazySalesOrderForm = React.lazy(() => import('../sales/salesOrderForm/SalesOrderForm'));
const LazyPurchaseOrderForm = React.lazy(() => import('../../views/purchases/PurchaseOrderForm'));
const LazySalesInvoiceForm = React.lazy(() => import('../salesLedger/SalesInvoiceForm'));
const LazyProductsGoodsReceiptForm = React.lazy(() => import('../product/ProductsGoodsReceiptForm'));

const { workspace: workspaceQuery, type, title } = DocumentSummary;

//Exported in case we want to use it elsewhere (Similar code is already in use in some class components, no need to convert them right now).
//Just gets the content width for a grid/workspace from a given proportion (e.g. '1 / 2').
export const useGridWidth = (proportion: number) => {
  const appMenuHidden = useSelector((store: any) => store.app.appMenuHidden);
  const menuWidth = appMenuHidden ? 45 : 255;
  return (window.innerWidth - menuWidth - 50) * proportion;
};

const LazyContent: FunctionComponent<any> = ({
  type,
  transactionId,
  companyId,
  ...props
}: {
  type: TargetEntity;
  transactionId: number;
  companyId: number;
} & any) => {
  const getIdProps = (id: number) => ({
    overrideParams: { id },
    match: { params: { id } },
  });

  switch (type) {
    case TargetEntity.Customer:
      return <LazyCustomerForm {...props} {...getIdProps(transactionId)} />;
    case TargetEntity.CustomerContact:
      return <LazyCustomerForm {...props} {...getIdProps(companyId)} activeTabId={'contacts'} />;
    case TargetEntity.SalesOrder:
      return <LazySalesOrderForm {...props} {...getIdProps(transactionId)} />;
    case TargetEntity.PurchaseOrder:
      return <LazyPurchaseOrderForm {...props} {...getIdProps(transactionId)} />;
    case TargetEntity.SalesInvoice:
      return <LazySalesInvoiceForm {...props} {...getIdProps(transactionId)} />;
    case TargetEntity.GoodsReceipt:
      return <LazyProductsGoodsReceiptForm {...props} {...getIdProps(transactionId)} />;
    default:
      return null;
  }
};

const TransactionEntityFormatter = ({ value, dependentValues }: any = {}) => {
  const [type, setType] = useState<any>(false);

  const viewContext = useMemo(
    () => ({
      uniqueName: 'TransactionEntityFormatter',
    }),
    []
  );

  const transactionModal = !!type ? (
    <FluentModal isOpen={!!type}>
      <div style={{ height: '90vh', width: '80vw', position: 'relative' }}>
        <ModalContext.Provider value={{ onClose: () => setType(null) }}>
          <ViewContextProvider value={viewContext}>
            <Suspense fallback={<ProgressIndicator />}>
              <LazyContent
                type={type}
                transactionId={dependentValues?.rowData?.transactionId}
                companyId={dependentValues?.rowData?.companyId}
                onClose={() => setType(null)}
              />
            </Suspense>
          </ViewContextProvider>
        </ModalContext.Provider>
      </div>
    </FluentModal>
  ) : null;

  const connectedForm = useContext(ConnectedFormContext) as Partial<ConnectedFormContextType>;

  //Avoid proving a recursive link (E.g. If we're already viewing a Customer, we don't want a link to view...the same Customer).
  if (connectedForm.entity?.type.toUpperCase() === value?.replace('_', '').replace('CONTACT', '').toUpperCase()) {
    return value;
  }

  return (
    <>
      {transactionModal}
      <div
        className={'textlink'}
        onClick={() => {
          setType(value);
        }}
      >
        {value}
      </div>
    </>
  );
};

const fieldNames: Array<Paths<IDocumentSummary>> = ['reportType', 'comment', 'createdAt', 'createdBy'];

const DocumentSummaryWorkspace = (
  props: Omit<WorkspaceConfig<IDocumentSummary>, 'viewScreenName'> & {
    preview?: boolean;
    embeddedView?: boolean;
    predefinedFilters: Array<IFilterInput>;
    removeAddButton?: boolean;
    viewScreenName?: string;
  }
) => {
  const [fileName, setFileName] = useState<Maybe<string>>(null);
  const [fileType, setFileType] = useState<Maybe<string>>(null);
  const gridWidth = useGridWidth(1 / 2);

  const handleRowClick = (rowData: IDocumentSummary) => {
    setFileName(rowData.fileName);
    setFileType(rowData.reportTemplate?.reportTemplateFormat === 'HANDLEBARS' ? 'html' : 'pdf');
  };

  const columnOverrides = useMemo(
    () => ({
      transactionEntity: {
        formatter: TransactionEntityFormatter,
      },
    }),
    []
  );

  const workspaceProps = useWorkspace<IDocumentSummary>(
    { ...props, viewScreenName: props.viewScreenName ?? DocumentSummary.workspaceUrl },
    DocumentSummary,
    {
      columns: fieldNames,
      columnOverrides: columnOverrides,
      initialSortColumn: 'createdAt',
      initialSortDirection: Direction.Desc,
      additionalQueryOptions: {
        onCompleted: () => {
          // If the query re-fetches (filters are changed) - Reset the selected file as may not be appropriate
          //   to the current filters (For example if connectedForm.values.id changes, we shouldn't show the file from the previous order).
          //
          // This does mean other filters also reset it (generic filter for example) but still believe this is a reasonable behaviour.
          setFileName(null);
          setFileType(null);
        },
      },
    }
  );

  return (
    <Form.Row style={{ marginLeft: 0, marginRight: 0 }}>
      <Form.Fieldset cols={6 / 12} style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Workspace
          entityType={type}
          title={title}
          defaultQuery={workspaceQuery}
          excludeSubModuleFromBreadcrumb={true}
          {...workspaceProps}
          {...props}
          entity={DocumentSummary}
          onRowClick={handleRowClick}
          contentWidth={gridWidth}
          contextKey={'document-summary'}
          viewScreenName={workspaceProps.viewScreenName}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={6 / 12} style={{ paddingLeft: 0, paddingRight: 0 }}>
        {fileType === 'pdf' ? (
          <embed
            src={`/api/resources/attachment/${fileName}`}
            key={`${fileName}`}
            width={'100%'}
            height={'100%'}
            style={{ marginTop: '14px' }}
            alt={'pdf'}
            title={'Document Preview'}
          />
        ) : (
          <iframe
            src={`/api/resources/attachment/${fileName}`}
            title={'HTML Preview'}
            sandbox=""
            style={{
              border: 'none',
              width: '100%',
              height: '100%',
            }}
          />
        )}
      </Form.Fieldset>
    </Form.Row>
  );
};

export default DocumentSummaryWorkspace;
